import { useState, useRef, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Textarea } from '../components/ui';

interface CopilotImage {
  url: string;
  prompt: string;
  revised_prompt?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  suggestedActions?: any[];
  image?: CopilotImage | null;
  timestamp: number;
}

interface Suggestion {
  id: string;
  title: string;
  description: string;
  prompt: string;
}

const quickActions = [
 { icon: '', label: 'Gerar Imagem', template: 'Quero gerar uma imagem de ' },
 { icon: '', label: 'Criar Música', template: 'Quero criar uma música no estilo ' },
 { icon: '', label: 'Pesquisar', template: 'Pesquisar sobre ' },
 { icon: '', label: 'Criar Vídeo', template: 'Quero criar um vídeo sobre ' },
 { icon: '', label: 'Gerar Código', template: 'Gerar código para ' },
];

function renderMarkdown(text: string) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:T.primaryBg;padding:2px 6px;border-radius:4px;font-size:13px">$1</code>')
    .replace(/^(\d+)\.\s+(.+)$/gm, '<div style="margin:4px 0;padding-left:8px">$1. $2</div>')
    .replace(/^[-•]\s+(.+)$/gm, '<div style="margin:4px 0;padding-left:8px">• $1</div>')
    .replace(/\n/g, '<br/>');
}

export function CopilotPage({ path }: { path?: string }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api.get<{ suggestions: Suggestion[] }>('/copilot/suggestions').then(d => setSuggestions(d.suggestions)).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: msg, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = '44px';

    try {
      const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const data = await api.post<{ reply: string; suggestedActions: any[]; generatedPrompt?: string; image?: CopilotImage | null }>('/copilot/chat', { message: msg, history });
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.reply,
        suggestedActions: data.suggestedActions,
        image: data.image || null,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
 content: 'Erro ao processar sua mensagem. Tente novamente.',
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const autoResize = (e: Event) => {
    const t = e.target as HTMLTextAreaElement;
    t.style.height = '44px';
    t.style.height = Math.min(t.scrollHeight, 120) + 'px';
  };

  const isWelcome = messages.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: T.bg, color: T.text, position: 'relative' }}>

      {/* Messages area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 140px', display: 'flex', flexDirection: 'column' }}>
        {isWelcome ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 32, maxWidth: 640, margin: '0 auto', width: '100%' }}>
            {/* Welcome title */}
            <div style={{ textAlign: 'center' }}>
 <div style={{ fontSize: 40, marginBottom: 8 }}></div>
              <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px', background: `linear-gradient(135deg, ${T.primary}, ${T.purple}, #EC4899)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Copilot
              </h1>
              <p style={{ fontSize: 16, color: T.textMuted, margin: 0, fontWeight: 500 }}>
                O que posso fazer por você?
              </p>
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {quickActions.map(a => (
                <Button variant="ghost"
                  key={a.label}
                  onClick={() => { setInput(a.template); textareaRef.current?.focus(); }}
                  style={{
                    background: T.bgCard, border: `1px solid var(--border, ${T.bgSecondary})`,
                    color: T.text, borderRadius: 20, padding: '8px 16px',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.background = T.primaryBg; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = `var(--border, ${T.bgSecondary})`; e.currentTarget.style.background = T.bgCard; }}
                >
                  <span>{a.icon}</span> {a.label}
                </Button>
              ))}
            </div>

            {/* Suggestion cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, width: '100%' }}>
              {suggestions.map(s => (
                <div
                  key={s.id}
                  onClick={() => send(s.prompt)}
                  style={{
                    background: T.bgCard, border: `1px solid var(--border, ${T.bgSecondary})`,
                    borderRadius: 14, padding: '16px', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = `var(--border, ${T.bgSecondary})`; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.4 }}>{s.description}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12, maxWidth: 720, width: '100%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', marginLeft: msg.role === 'user' ? 'auto' : 0, marginRight: msg.role === 'user' ? 0 : 'auto' }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '12px 16px',
                  borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  background: msg.role === 'user' ? `linear-gradient(135deg, ${T.primary}, ${T.purple})` : T.bgCard,
                  border: msg.role === 'assistant' ? `1px solid ${T.border}` : `none`,
                  fontSize: 14, lineHeight: 1.6,
                }}>
                  {msg.role === 'assistant' ? (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                      {msg.image?.url && (
                        <div style={{ marginTop: 12 }}>
                          <img
                            src={msg.image.url}
                            alt={msg.image.prompt || 'Generated image'}
                            style={{
                              maxWidth: '100%',
                              borderRadius: 12,
                              border: `1px solid var(--border, ${T.bgSecondary})`,
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(msg.image!.url, '_blank')}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <span>{msg.content}</span>
                  )}
                  {/* Suggested actions */}
                  {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid var(--border, ${T.bgSecondary})` }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ações relacionadas</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {msg.suggestedActions.map((a: any) => (
                          <span key={a.id} style={{
                            background: T.primaryBg, border: `1px solid ${T.primaryBg}`,
                            borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: T.primary,
                          }}>
                            {a.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {/* Typing indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                <div style={{
                  background: T.bgCard, border: `1px solid var(--border, ${T.bgSecondary})`,
                  borderRadius: '18px 18px 18px 4px', padding: '12px 20px', display: 'flex', gap: 6, alignItems: 'center',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, animation: 'copilotDot 1.4s ease-in-out infinite' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, animation: 'copilotDot 1.4s ease-in-out 0.2s infinite' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary, animation: 'copilotDot 1.4s ease-in-out 0.4s infinite' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: T.bg, borderTop: `1px solid var(--border, ${T.bgSecondary})`,
        padding: '12px 16px 20px', zIndex: 10,
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <Textarea
            ref={textareaRef}
            value={input}
            onInput={(e: any) => { setInput(e.target.value); autoResize(e); }}
            onKeyDown={handleKeyDown}
            placeholder="Descreva o que você precisa..."
            rows={1}
            style={{
              flex: 1, background: T.bgCard, border: `1px solid var(--border, ${T.bgSecondary})`,
              borderRadius: 14, padding: '12px 16px', fontSize: 14, color: T.text,
              resize: 'none', outline: 'none', fontFamily: 'inherit', minHeight: 44, maxHeight: 120,
              lineHeight: 1.5, transition: 'border-color 0.15s',
            }}
            onFocus={(e: any) => e.target.style.borderColor = T.primary}
            onBlur={(e: any) => e.target.style.borderColor = `var(--border, ${T.bgSecondary})`}
          />
          <Button variant="ghost"
            onClick={() => send()}
            disabled={!input.trim() || loading}
            style={{
              width: 44, height: 44, borderRadius: 12, border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              background: input.trim() && !loading ? `linear-gradient(135deg, ${T.primary}, ${T.purple})` : T.bgCard,
              color: T.text, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s', opacity: input.trim() && !loading ? 1 : 0.5, flexShrink: 0,
            }}
          >
            ▲
          </Button>
        </div>
      </div>

      {/* Keyframe animation for typing dots */}
      <style>{`
        @keyframes copilotDot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
